import React, { ReactElement, ReactNode } from 'react';
import { Box } from '@mui/material';

import {
  Paper,
  Button,
  TableContainer,
  KeyboardArrowLeftIcon,
  KeyboardArrowRightIcon,
  CircularProgress,
} from '@occc/ui-design-system';
import { RequestStatus, RequestOptions } from '@occc-app/lib/constants';

import RecordManagement, { IRecordManagement } from './RecordManagement';
import Search from './Search';

import { State } from '../types';

type Child = ReactElement<RecordManagement> | ReactNode;
type Props = {
  fetch: (options?: RequestOptions) => void;
  changePath: (path: string) => void;
  requestState: State;
  children: Child | Child[];
};

export class PageableRequest extends React.Component<Props, {}> {
  static readonly RecordManagement: typeof RecordManagement = RecordManagement;
  static readonly Search: typeof Search = Search;

  componentDidMount() {
    this.props.fetch();
  }

  handleFetch = (path?: string) => (ev: React.MouseEvent<HTMLElement>) => {
    ev.preventDefault();
    if (path) {
      this.props.changePath(path);
    }
    this.props.fetch();
  };

  renderResults(remainingChildren: ReactNode[]) {
    const { requestState } = this.props;
    const { next, prev } = requestState;

    if (requestState.status === RequestStatus.WAIT) {
      return <CircularProgress size={24} />;
    } else if (requestState.status === RequestStatus.OK) {
      if (!requestState.records) {
        return <>{remainingChildren}</>;
      }
      return (
        <TableContainer>
          {remainingChildren}
          <Box display='flex' alignItems='center' justifyContent='flex-end' minHeight={52} pr={2}>
            <Button
              startIcon={<KeyboardArrowLeftIcon />}
              onClick={this.handleFetch(prev)}
              disabled={!prev}
              size='small'
            >
              prev
            </Button>
            <Button
              endIcon={<KeyboardArrowRightIcon />}
              onClick={this.handleFetch(next)}
              disabled={!next}
              size='small'
            >
              next
            </Button>
          </Box>
        </TableContainer>
      );
    }
    return <span>ERROR</span>;
  }

  wrapFilterComponent = (child: ReactElement<any>) => {
    const onChange = (...args: any[]) => {
      this.props.changePath('');
      child.props.onChange(...args);
      this.props.fetch();
    };

    return React.cloneElement(child, { ...child.props, onChange }, child.props.children);
  };

  renderRecordManagement = (recordManagement: ReactElement<IRecordManagement> | null) => {
    if (recordManagement === null) {
      return;
    }

    const filters = React.Children.map(recordManagement.props.children, this.wrapFilterComponent);
    const wrappedRecordManagement = React.cloneElement(
      recordManagement,
      recordManagement.props,
      filters,
    );

    return (
      <Paper variant='outlined' sx={{ p: 1, mb: 2 }}>
        <Box>{wrappedRecordManagement}</Box>
      </Paper>
    );
  };

  render() {
    let recordManagement: ReactElement<IRecordManagement> | null = null;
    const remainingChildren: ReactNode[] = [];

    React.Children.forEach(this.props.children, (child: ReactElement<any>) => {
      if (child.type === RecordManagement) {
        recordManagement = child;
      } else {
        remainingChildren.push(child);
      }
    });
    return (
      <Box width='100%'>
        {this.renderRecordManagement(recordManagement)}
        {this.renderResults(remainingChildren)}
      </Box>
    );
  }
}
